import React, { useEffect, useMemo, useRef, useState } from 'react';

// The Regulation desk: a conversation over the apps the person ticked in the
// launcher. It talks to /api/lane as the signed-in person. Every answer can
// show where it came from and what was left out. App names come from the
// server with the tick list; the launcher leaves them beside the keys.
// Layout follows the rest of Core: white cards on slate, orange for the one
// action that moves things forward, small uppercase section labels.
const API = process.env.REACT_APP_API_URL;
function readLabels() {
  try { const v = JSON.parse(sessionStorage.getItem('lane-app-labels') || '{}'); return v && typeof v === 'object' ? v : {}; } catch { return {}; }
}
// The names the server gives the apps; the launcher's copy is the first
// word, the server's list fills in the rest (older conversations too).
function useAppLabels() {
  const [labels, setLabels] = useState(readLabels);
  useEffect(() => {
    let dead = false;
    api('GET', '/api/lane/apps').then((d) => { if (!dead) setLabels((l) => ({ ...Object.fromEntries((d.apps || []).map((a) => [a.key, a.label])), ...l })); }).catch(() => {});
    return () => { dead = true; };
  }, []);
  return labels;
}

function readApps() {
  try { const v = JSON.parse(sessionStorage.getItem('lane-apps') || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
}

async function api(method, path, body) {
  const res = await fetch(`${API}${path}`, { method, credentials: 'include', headers: body ? { 'Content-Type': 'application/json' } : {}, body: body ? JSON.stringify(body) : undefined });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) throw new Error(data.message || `Request failed (${res.status})`);
  return data;
}

// Conversations grouped the way people look for them: today, this week, earlier.
function groupByAge(list) {
  const now = new Date(); const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const weekAgo = startToday - 6 * 86400000;
  const groups = [['Today', []], ['Previous 7 days', []], ['Earlier', []]];
  for (const c of list) {
    const t = new Date(c.updated_at || c.created_at || 0).getTime();
    groups[t >= startToday ? 0 : t >= weekAgo ? 1 : 2][1].push(c);
  }
  return groups.filter(([, items]) => items.length);
}

// While the desk works: the PCG beacon breathing, a line that moves through
// what the desk actually does (search, read, check), and a slow orange sweep.
// The lines are the steps every answer goes through, in order; they are not
// live progress, so the wording stays honest ("reading", never "found").
const STEPS = ['Searching', 'Reading what came back', 'Checking every sentence against the source', 'Writing the answer'];
function Beacon({ size = 28, still = false }) {
  return (
    <span className="relative flex items-center justify-center flex-shrink-0" style={{ width: size, height: size }}>
      <span className="absolute inset-0 rounded-full bg-orange-500/15" style={still ? undefined : { animation: 'pcgBeacon 1.8s ease-in-out infinite' }} />
      <span className="absolute rounded-full bg-orange-500/25" style={{ width: size / 2, height: size / 2, ...(still ? {} : { animation: 'pcgBeacon 1.8s ease-in-out .3s infinite' }) }} />
      <span className="relative w-2 h-2 rounded-full bg-orange-500" />
    </span>
  );
}
function Thinking({ scope }) {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => Math.min(x + 1, STEPS.length - 1)), 2600); return () => clearInterval(t); }, []);
  return (
    <div className="flex items-start gap-3">
      <Beacon />
      <div className="relative overflow-hidden rounded-2xl px-4 py-3 bg-white border border-slate-200 shadow-sm min-w-[300px]">
        <div className="pcg-anim">
          <div className="text-[13px] text-slate-700">{STEPS[i]}{i === 0 ? ` ${scope}` : ''}
            <span className="inline-flex ml-1 gap-0.5 align-middle">
              {[0, 1, 2].map((d) => <span key={d} className="w-1 h-1 rounded-full bg-slate-400" style={{ animation: `pcgDot 1.2s ${d * 0.2}s infinite` }} />)}
            </span>
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">Nothing is shown until it is checked.</div>
        </div>
        <span className="pointer-events-none absolute bottom-0 left-0 h-[2px] w-1/3 bg-gradient-to-r from-transparent via-orange-500 to-transparent" style={{ animation: 'pcgSweep 2.2s linear infinite' }} />
      </div>
    </div>
  );
}

// Erase, confirmed in place (no browser dialog): what goes and what stays.
function EraseConfirm({ onErase, onCancel, busy }) {
  return (
    <div className="mt-1.5 rounded-xl bg-rose-50 ring-1 ring-rose-200 px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
      <div className="text-[12px] text-slate-700 leading-snug">Erase this conversation? Its messages and anything still waiting for your decision go. Anything you already approved stays where it landed.</div>
      <div className="mt-2 flex items-center gap-2">
        <button type="button" disabled={busy} onClick={onErase} className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50">{busy ? 'Erasing…' : 'Erase'}</button>
        <button type="button" disabled={busy} onClick={onCancel} className="text-[12px] font-semibold text-slate-500 hover:text-slate-700">Cancel</button>
      </div>
    </div>
  );
}

export default function LaneChat({ user }) {
  const LABELS = useAppLabels();
  const [apps, setApps] = useState(readApps);
  const [available, setAvailable] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [current, setCurrent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [open, setOpen] = useState({});
  const [filter, setFilter] = useState('');
  const [confirmErase, setConfirmErase] = useState(null);
  const [erasing, setErasing] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    api('GET', '/api/lane/apps').then((d) => {
      setAvailable(d.apps || []);
      setApps((prev) => (prev.length ? prev.filter((k) => (d.apps || []).some((a) => a.key === k)) : (d.apps || []).map((a) => a.key)));
    }).catch((e) => setError(e.message));
    api('GET', '/api/lane/conversations').then((d) => setConversations(d.conversations || [])).catch(() => {});
  }, []);

  // Scroll the message list only; scrolling an element into view would move the whole app page under the top bar.
  useEffect(() => { const el = scrollRef.current; if (el && (messages.length || busy)) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); }, [messages, busy]);
  useEffect(() => { if (!notice) return undefined; const t = setTimeout(() => setNotice(''), 4000); return () => clearTimeout(t); }, [notice]);

  const decide = async (msgId, proposalId, verb, note) => {
    try {
      const d = await api('POST', `/api/lane/proposals/${proposalId}/${verb}`, { note: note || undefined });
      setMessages((m) => m.map((x) => x.id !== msgId ? x : { ...x, proposals: (x.proposals || []).map((p) => p.id !== proposalId ? p : { ...p, status: verb === 'approve' ? 'approved' : 'dismissed', result: d.result || null }) }));
    } catch (e) { setError(e.message); }
  };

  const openConversation = async (id) => {
    try {
      const d = await api('GET', `/api/lane/conversations/${id}`);
      const props = await api('GET', `/api/lane/conversations/${id}/proposals`).then((x) => x.proposals || []).catch(() => []);
      const msgs = (d.messages || []).map((m) => ({ ...m, proposals: props.filter((p) => p.message_id === m.id) }));
      setCurrent(d.conversation); setApps(d.conversation.apps); setMessages(msgs); setError(''); setConfirmErase(null);
      setTimeout(() => inputRef.current?.focus(), 30);
    } catch (e) { setError(e.message); }
  };

  const startNew = () => { setCurrent(null); setMessages([]); setError(''); setConfirmErase(null); setTimeout(() => inputRef.current?.focus(), 30); };

  const erase = async (id) => {
    setErasing(true); setError('');
    try {
      const d = await api('DELETE', `/api/lane/conversations/${id}`);
      setConversations((list) => list.filter((c) => c.id !== id));
      if (current?.id === id) { setCurrent(null); setMessages([]); }
      setConfirmErase(null);
      setNotice(`Conversation erased${d.erased?.approved ? `; ${d.erased.approved} approved item${d.erased.approved === 1 ? '' : 's'} stay in ${d.erased.approved === 1 ? 'its app' : 'their apps'}` : ''}.`);
    } catch (e) { setError(e.message); }
    setErasing(false);
  };

  const send = async () => {
    const q = text.trim();
    if (!q || busy) return;
    setBusy(true); setError('');
    try {
      let conv = current;
      if (!conv) {
        const d = await api('POST', '/api/lane/conversations', { apps });
        conv = d.conversation; setCurrent(conv);
        setConversations((list) => [{ ...conv, title: q.slice(0, 120), updated_at: new Date().toISOString() }, ...list]);
      }
      setMessages((m) => [...m, { id: `u-${Date.now()}`, role: 'user', content: q }]);
      setText('');
      const d = await api('POST', `/api/lane/conversations/${conv.id}/messages`, { text: q });
      const r = d.reply;
      setMessages((m) => [...m, { id: r.id, role: 'assistant', content: r.answer, sources: r.sources, removed: r.removed, outside: r.outside, usage: r.usage, proposals: r.proposals || [] }]);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  };

  const toggleApp = (key) => {
    if (current) return; // the apps of an open conversation do not change; start a new one
    setApps((a) => (a.includes(key) ? a.filter((k) => k !== key) : [...a, key]));
  };

  const scopeLabel = apps.map((k) => LABELS[k] || k).join(', ') || 'no app selected';
  const isCompliance = user?.can_see_privacy === true || user?.is_compliance_team === true;
  const shown = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return f ? conversations.filter((c) => String(c.title || '').toLowerCase().includes(f)) : conversations;
  }, [conversations, filter]);
  const groups = useMemo(() => groupByAge(shown), [shown]);

  return (
    <div className="flex h-full min-h-0 overflow-hidden bg-slate-50 text-slate-800">
      <style>{`
        @keyframes pcgBeacon { 0%,100% { transform: scale(1); opacity: .55 } 50% { transform: scale(1.35); opacity: 1 } }
        @keyframes pcgSweep { 0% { transform: translateX(-100%) } 100% { transform: translateX(260%) } }
        @keyframes pcgDot { 0%,80%,100% { opacity: .25 } 40% { opacity: 1 } }
        @media (prefers-reduced-motion: reduce) { .pcg-anim *, .pcg-anim { animation: none !important } }
      `}</style>

      {/* Conversations */}
      <aside className="w-[280px] flex-shrink-0 border-r border-slate-200 bg-white flex flex-col min-h-0">
        <div className="px-4 pt-5 pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Beacon size={22} still />
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Regulation desk</div>
          </div>
          <button type="button" onClick={startNew} className="mt-3 w-full rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-[13px] font-semibold py-2.5 shadow-sm transition-colors">New conversation</button>
          {conversations.length > 0 && (
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search your conversations"
              className="mt-3 w-full text-[12px] rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 outline-none focus:border-orange-300 focus:bg-white" />
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-3">
          {conversations.length === 0 ? (
            <p className="px-3 text-[12px] text-slate-400">Your conversations will appear here.</p>
          ) : !shown.length ? (
            <p className="px-3 text-[12px] text-slate-400">No conversation matches.</p>
          ) : groups.map(([label, items]) => (
            <div key={label} className="mb-3">
              <div className="px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
              {items.map((c) => {
                const active = current?.id === c.id;
                return (
                  <div key={c.id} className="group relative mb-0.5">
                    <button type="button" onClick={() => openConversation(c.id)}
                      className={`w-full text-left pl-3 pr-9 py-2.5 rounded-xl transition-colors ${active ? 'bg-orange-50 ring-1 ring-orange-200' : 'hover:bg-slate-50'}`}>
                      <div className={`text-[13px] truncate ${active ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>{c.title || 'Conversation'}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5 truncate">{(c.apps || []).map((k) => LABELS[k] || k).join(' · ')}</div>
                    </button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setConfirmErase(confirmErase === 'list:' + c.id ? null : 'list:' + c.id); }}
                      title="Erase this conversation" aria-label="Erase this conversation"
                      className={`absolute right-2 top-2.5 w-6 h-6 flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-opacity ${confirmErase === 'list:' + c.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}`}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /></svg>
                    </button>
                    {confirmErase === 'list:' + c.id && <div className="px-1"><EraseConfirm busy={erasing} onErase={() => erase(c.id)} onCancel={() => setConfirmErase(null)} /></div>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </aside>

      {/* Conversation */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <header className="px-6 py-4 border-b border-slate-200 bg-white">
          <div className="max-w-[860px] mx-auto flex items-center gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{current ? 'Conversation' : 'New conversation'}</div>
              <div className="text-[16px] font-bold text-slate-900 truncate">{current ? (conversations.find((c) => c.id === current.id)?.title || 'Conversation') : `Ask across ${scopeLabel}`}</div>
            </div>
            <div className="flex items-center gap-1.5 ml-auto flex-wrap">
              {available.map((a) => {
                const on = apps.includes(a.key);
                return (
                  <button key={a.key} type="button" onClick={() => toggleApp(a.key)} disabled={!!current}
                    title={current ? 'Start a new conversation to change the apps' : (on ? `Leave ${a.label} out` : `Include ${a.label}`)}
                    className={`text-[12px] font-semibold px-3 py-1 rounded-full ring-1 transition ${on ? 'bg-orange-50 text-orange-700 ring-orange-300' : 'bg-white text-slate-400 ring-slate-200 hover:ring-slate-300'} ${current ? 'cursor-default' : ''}`}>
                    {on ? '✓ ' : ''}{a.label}
                  </button>
                );
              })}
              {current && (
                <button type="button" onClick={() => setConfirmErase(confirmErase === 'head:' + current.id ? null : 'head:' + current.id)}
                  className="ml-1 text-[12px] font-semibold px-3 py-1 rounded-full ring-1 ring-slate-200 text-slate-500 hover:text-rose-700 hover:ring-rose-200 bg-white">Erase</button>
              )}
            </div>
          </div>
          {current && confirmErase === 'head:' + current.id && <div className="max-w-[860px] mx-auto"><EraseConfirm busy={erasing} onErase={() => erase(current.id)} onCancel={() => setConfirmErase(null)} /></div>}
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6">
          <div className="max-w-[860px] mx-auto space-y-5">
            {notice && <div className="rounded-xl bg-emerald-50 ring-1 ring-emerald-200 px-4 py-2 text-[12.5px] font-semibold text-emerald-800">{notice}</div>}
            {messages.length === 0 && !busy && (
              <div className="py-10">
                <div className="flex flex-col items-center text-center">
                  <Beacon size={44} />
                  <p className="mt-4 text-[19px] font-bold text-slate-900">What do you want to know?</p>
                  <p className="text-[13px] text-slate-500 mt-2 max-w-[540px]">Ask about a memo, a deadline, what the new rule changes, which policy covers it, or what was decided and by whom. Answers come only from what is in {scopeLabel}. If it is not there, you will be told so.</p>
                </div>
              </div>
            )}
            {messages.map((m) => (m.role === 'user' ? (
              <div key={m.id} className="flex justify-end">
                <div className="max-w-[80%] rounded-2xl rounded-br-md px-4 py-3 text-[14px] leading-relaxed bg-slate-900 text-white whitespace-pre-wrap shadow-sm">{m.content}</div>
              </div>
            ) : (
              <div key={m.id} className="flex items-start gap-3">
                <Beacon still />
                <div className="flex-1 min-w-0 max-w-[88%] rounded-2xl rounded-tl-md px-5 py-4 text-[14px] leading-relaxed bg-white border border-slate-200 shadow-sm text-slate-800">
                  {m.outside && <div className="mb-2 inline-flex px-2 py-0.5 rounded-full bg-slate-100 text-[10.5px] font-semibold text-slate-500">Not in the apps you picked</div>}
                  <div className="whitespace-pre-wrap">{m.content}</div>
                  {!m.outside && (m.sources?.length > 0 || m.removed?.length > 0) && (
                    <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                      {m.sources?.length > 0 && (
                        <button type="button" className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-full ring-1 transition ${open[m.id] === 'sources' ? 'bg-orange-50 text-orange-700 ring-orange-200' : 'bg-white text-slate-500 ring-slate-200 hover:ring-slate-300'}`} onClick={() => setOpen((o) => ({ ...o, [m.id]: o[m.id] === 'sources' ? null : 'sources' }))}>
                          Where is that from · {m.sources.length}
                        </button>
                      )}
                      {isCompliance && m.removed?.length > 0 && (
                        <button type="button" className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-full ring-1 transition ${open[m.id] === 'removed' ? 'bg-slate-100 text-slate-700 ring-slate-300' : 'bg-white text-slate-500 ring-slate-200 hover:ring-slate-300'}`} onClick={() => setOpen((o) => ({ ...o, [m.id]: o[m.id] === 'removed' ? null : 'removed' }))}>
                          What was left out · {m.removed.length}
                        </button>
                      )}
                    </div>
                  )}
                  {open[m.id] === 'sources' && (
                    <ol className="mt-3 space-y-2">
                      {m.sources.map((s, i) => (
                        <li key={i} className="flex gap-2.5 rounded-xl bg-slate-50 ring-1 ring-slate-100 px-3 py-2">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-orange-500 text-white text-[10.5px] font-bold flex items-center justify-center">{i + 1}</span>
                          <div className="min-w-0 text-[12px]">
                            <div className="font-semibold text-slate-700">{s.label}</div>
                            <div className="text-slate-500 italic mt-0.5">“{s.quote}”</div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                  {open[m.id] === 'removed' && (
                    <ul className="mt-3 space-y-2">
                      {m.removed.map((r, i) => (
                        <li key={i} className="rounded-xl bg-slate-50 ring-1 ring-slate-100 px-3 py-2 text-[12px] text-slate-600">
                          <span className="line-through text-slate-400">{r.sentence}</span>
                          <span className="block text-slate-500 mt-0.5">Left out: {r.reason}.</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {(m.proposals || []).map((p) => (
                    <div key={p.id} className="mt-3 rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Proposed action</span>
                        {p.status !== 'proposed' && <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${p.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{p.status === 'approved' ? 'Approved' : 'Dismissed'}</span>}
                      </div>
                      <div className="mt-1 text-[13.5px] font-semibold text-slate-900">{p.preview?.title}</div>
                      <dl className="mt-2 space-y-1">
                        {(p.preview?.lines || []).map(([k, v], i) => (
                          <div key={i} className="grid grid-cols-[120px_1fr] gap-2 text-[12px]">
                            <dt className="text-slate-500">{k}</dt>
                            <dd className="text-slate-800 whitespace-pre-wrap">{v}</dd>
                          </div>
                        ))}
                      </dl>
                      {p.preview?.note && <p className="mt-2 text-[11px] text-slate-500">{p.preview.note}</p>}
                      <div className="mt-3 flex items-center gap-2">
                        {p.status === 'proposed' ? (
                          <>
                            <button type="button" onClick={() => decide(m.id, p.id, 'approve')} className="text-[12px] font-semibold px-3.5 py-1.5 rounded-lg bg-orange-500 text-white hover:bg-orange-600">Approve</button>
                            <button type="button" onClick={() => { const note = window.prompt('Why not? (optional)') ?? null; if (note !== null) decide(m.id, p.id, 'dismiss', note); }} className="text-[12px] font-semibold px-3.5 py-1.5 rounded-lg bg-white ring-1 ring-slate-200 text-slate-600 hover:ring-slate-300">Dismiss</button>
                            <span className="text-[11px] text-slate-400">Nothing happens until you approve.</span>
                          </>
                        ) : p.status === 'approved' ? (
                          <span className="text-[12px] font-semibold text-emerald-700">Done: {p.result?.created || 'approved'}</span>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )))}
            {busy && <Thinking scope={scopeLabel} />}
            {error && <div className="rounded-xl bg-rose-50 ring-1 ring-rose-200 px-4 py-2 text-[13px] text-rose-700">{error}</div>}
          </div>
        </div>

        <div className="border-t border-slate-200 bg-white px-6 py-4">
          <div className="max-w-[860px] mx-auto">
            <div className="flex items-end gap-2 rounded-2xl border border-slate-200 bg-slate-50 focus-within:border-orange-300 focus-within:bg-white focus-within:shadow-sm transition-all p-2">
              <textarea ref={inputRef} value={text} onChange={(e) => setText(e.target.value)} rows={2}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={apps.length ? `Ask about ${scopeLabel}…` : 'Pick at least one app above'}
                disabled={!apps.length || busy}
                className="flex-1 resize-none bg-transparent outline-none px-3 py-2 text-[14px] disabled:opacity-60" />
              <button type="button" onClick={send} disabled={!text.trim() || busy || !apps.length}
                className="rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-[13px] font-semibold px-5 py-2.5 disabled:opacity-40 transition-colors">Ask</button>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Enter to send, Shift and Enter for a new line. Answers are checked against the documents before they are shown. Nothing is sent outside the organization from here.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
